import { config } from '@/config';
import type { BandId } from '@/domain/bands';
import type { Coords } from '@/domain/geo';
import { normalizePhone } from '@/domain/phone';
import { collectUtmParams } from '@/services/utm';

export type Lead = {
  firstName: string;
  phone: string;
  cep: string | null;
  coords: Coords;
  band: BandId;
  nearestKm: number;
  nearestAddress: string;
  inputType: 'cep' | 'geolocation';
  locale: string;
};

export type LeadResult = 'ok' | 'failed';

const COUNTRY_PREFIX = '+55';

/* Formbricks accepts a response from any origin, because a link survey is answered by
   the public. That is what lets this page own the form instead of sending the lead to a
   hosted one, which would read as a different site at the moment we ask for the number. */
export const submitLead = async (
  lead: Lead,
  fetchImpl: typeof fetch = fetch,
): Promise<LeadResult> => {
  const base = {
    first_name: lead.firstName.trim(),
    phone: `${COUNTRY_PREFIX}${normalizePhone(lead.phone)}`,
    latitude: lead.coords.lat,
    longitude: lead.coords.lon,
    band: lead.band,
    nearest_km: lead.nearestKm,
    nearest_address: lead.nearestAddress,
    input_type: lead.inputType,
    locale: lead.locale,
    /* No page URL. A WAF in front of Vammo Forms answers 403 to a body carrying certain
       URL patterns, with no CORS header, so the browser reads it as a CORS failure and
       the lead is lost. The campaign parameters below already say where it came from. */
    ...collectUtmParams(window.location.search),
  };

  /* Omitted rather than sent empty when the lead used the device location: an empty
     hidden field reads as a CEP nobody filled in, not as one that never existed. */
  const data = lead.cep === null ? base : { ...base, cep: lead.cep };

  try {
    const response = await fetchImpl(
      `${config.formsUrl}/api/v1/client/${config.formsWorkspaceId}/responses`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ surveyId: config.notifySurveyId, finished: true, data }),
      },
    );
    return response.ok ? 'ok' : 'failed';
  } catch {
    return 'failed';
  }
};
